import React, { useEffect, useMemo, useState } from "react";
import { TopToolbar, CreateButton } from "react-admin";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, orderBy, query, where } from "firebase/firestore";

import { db } from "../../firebase/firebase";
import deleteTransaction from "./transactionDelete";
import {
  exportTransactionRegisterExcel,
  exportTransactionRegisterPDF,
  formatMoney,
  printTransactionRegister,
} from "../../utils/transactionRegisterExport";

const emptyFilters = {
  dateFrom: "",
  dateTo: "",
  account: "",
  type: "",
  paidBy: "",
  search: "",
  amountFrom: "",
  amountTo: "",
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "8px 9px",
  border: "1px solid #c8c8c8",
  borderRadius: 4,
  background: "white",
};

const buttonStyle = {
  border: "1px solid #1e4db7",
  background: "#1e4db7",
  color: "white",
  padding: "8px 13px",
  borderRadius: 4,
  cursor: "pointer",
  fontWeight: 600,
};

const uniqueValues = (records, field) =>
  [...new Set(records.map((record) => record[field]).filter(Boolean))].sort((a, b) =>
    String(a).localeCompare(String(b))
  );

export default function TransactionList() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(emptyFilters);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const navigate = useNavigate();

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "transactions"),
        where("isMirror", "==", false),
        orderBy("date", "desc")
      );
      const snapshot = await getDocs(q);
      setRecords(snapshot.docs.map((document) => ({ id: document.id, ...document.data() })));
    } catch (error) {
      console.error("Error loading transactions:", error);
      window.alert(`Unable to load transactions: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const accountOptions = useMemo(() => uniqueValues(records, "accountName"), [records]);
  const typeOptions = useMemo(() => uniqueValues(records, "expenseType"), [records]);
  const paidByOptions = useMemo(() => uniqueValues(records, "paidBy"), [records]);

  const filteredRecords = useMemo(() => {
    const text = filters.search.trim().toLowerCase();
    const minAmount = filters.amountFrom === "" ? null : Number(filters.amountFrom);
    const maxAmount = filters.amountTo === "" ? null : Number(filters.amountTo);

    return records.filter((record) => {
      if (filters.dateFrom && record.date < filters.dateFrom) return false;
      if (filters.dateTo && record.date > filters.dateTo) return false;
      if (filters.account && record.accountName !== filters.account) return false;
      if (filters.type && record.expenseType !== filters.type) return false;
      if (filters.paidBy && record.paidBy !== filters.paidBy) return false;

      const transactionAmount = Math.max(Number(record.amountIn || 0), Number(record.amountOut || 0));
      if (minAmount !== null && transactionAmount < minAmount) return false;
      if (maxAmount !== null && transactionAmount > maxAmount) return false;

      if (text) {
        const haystack = [
          record.date,
          record.accountName,
          record.expenseType,
          record.description,
          record.paidBy,
          record.comment,
          record.amountIn,
          record.amountOut,
          record.id,
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(text)) return false;
      }
      return true;
    });
  }, [records, filters]);

  const selectedRecords = useMemo(
    () => filteredRecords.filter((record) => selectedIds.has(record.id)),
    [filteredRecords, selectedIds]
  );

  const exportRows = selectedRecords.length > 0 ? selectedRecords : filteredRecords;
  const totalIn = filteredRecords.reduce((sum, row) => sum + Number(row.amountIn || 0), 0);
  const totalOut = filteredRecords.reduce((sum, row) => sum + Number(row.amountOut || 0), 0);
  const allVisibleSelected = filteredRecords.length > 0 && filteredRecords.every((row) => selectedIds.has(row.id));

  const updateFilter = (field, value) => setFilters((current) => ({ ...current, [field]: value }));

  const toggleSelectAll = () => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (allVisibleSelected) filteredRecords.forEach((row) => next.delete(row.id));
      else filteredRecords.forEach((row) => next.add(row.id));
      return next;
    });
  };

  const toggleRow = (id) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const resetFilters = () => {
    setFilters(emptyFilters);
    setSelectedIds(new Set());
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`Delete transaction dated ${record.date || "-"}?`)) return;
    try {
      await deleteTransaction(record.id);
      setSelectedIds((current) => {
        const next = new Set(current);
        next.delete(record.id);
        return next;
      });
      await fetchTransactions();
    } catch (error) {
      console.error("Delete failed:", error);
      window.alert(`Delete failed: ${error.message}`);
    }
  };

  if (loading) return <p style={{ padding: 20 }}>Loading transactions...</p>;

  return (
    <div style={{ padding: 20, maxWidth: "100%", overflowX: "auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 15 }}>
        <div>
          <h2 style={{ margin: 0 }}>Transaction Register</h2>
          <div style={{ color: "#666", marginTop: 4 }}>Filter, select, print, and export posted transactions.</div>
        </div>
        <TopToolbar><CreateButton /></TopToolbar>
      </div>

      <div style={{ border: "1px solid #d9d9d9", borderRadius: 6, padding: 14, marginBottom: 14, background: "#fafafa" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(170px, 1fr))", gap: 10 }}>
          <label>From date<input type="date" value={filters.dateFrom} onChange={(e) => updateFilter("dateFrom", e.target.value)} style={inputStyle} /></label>
          <label>To date<input type="date" value={filters.dateTo} onChange={(e) => updateFilter("dateTo", e.target.value)} style={inputStyle} /></label>
          <label>Account<select value={filters.account} onChange={(e) => updateFilter("account", e.target.value)} style={inputStyle}><option value="">All accounts</option>{accountOptions.map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
          <label>Type<select value={filters.type} onChange={(e) => updateFilter("type", e.target.value)} style={inputStyle}><option value="">All types</option>{typeOptions.map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
          <label>Paid by<select value={filters.paidBy} onChange={(e) => updateFilter("paidBy", e.target.value)} style={inputStyle}><option value="">All payment sources</option>{paidByOptions.map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
          <label>Search<input value={filters.search} onChange={(e) => updateFilter("search", e.target.value)} placeholder="Description, amount, account, comment..." style={inputStyle} /></label>
          <label>Amount from<input type="number" step="0.01" value={filters.amountFrom} onChange={(e) => updateFilter("amountFrom", e.target.value)} style={inputStyle} /></label>
          <label>Amount to<input type="number" step="0.01" value={filters.amountTo} onChange={(e) => updateFilter("amountTo", e.target.value)} style={inputStyle} /></label>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 13, alignItems: "center" }}>
          <button onClick={resetFilters} style={{ ...buttonStyle, background: "white", color: "#1e4db7" }}>Reset</button>
          <button onClick={() => printTransactionRegister(exportRows, filters, selectedRecords.length ? "Selected Transactions" : "Transaction Register")} style={buttonStyle}>Print {selectedRecords.length ? `Selected (${selectedRecords.length})` : "Filtered"}</button>
          <button onClick={() => exportTransactionRegisterPDF(exportRows, filters, selectedRecords.length ? "Selected Transactions" : "Transaction Register")} style={buttonStyle}>PDF</button>
          <button onClick={() => exportTransactionRegisterExcel(exportRows, filters, selectedRecords.length ? "Selected Transactions" : "Transaction Register")} style={buttonStyle}>Excel</button>
          <span style={{ marginLeft: "auto", fontWeight: 600 }}>{filteredRecords.length} record(s) | {selectedRecords.length} selected</span>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(180px, 1fr))", gap: 10, marginBottom: 12 }}>
        <div style={{ padding: 12, background: "#edf7ed", border: "1px solid #b7d8b7", borderRadius: 5 }}><b>Total In</b><div style={{ fontSize: 19 }}>AED {formatMoney(totalIn)}</div></div>
        <div style={{ padding: 12, background: "#fff1f0", border: "1px solid #efc0bc", borderRadius: 5 }}><b>Total Out</b><div style={{ fontSize: 19 }}>AED {formatMoney(totalOut)}</div></div>
        <div style={{ padding: 12, background: "#eef3ff", border: "1px solid #bac8e8", borderRadius: 5 }}><b>Net Movement</b><div style={{ fontSize: 19 }}>AED {formatMoney(totalIn - totalOut)}</div></div>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 1350 }}>
        <thead style={{ backgroundColor: "#f0f3f8" }}>
          <tr>
            <th style={{ border: "1px solid #bbb", padding: 7 }}><input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} aria-label="Select all filtered transactions" /></th>
            {["Date", "Account Head", "Type", "Description", "Amount In", "Amount Out", "Balance", "Paid By", "Comment", "Proof", "Actions"].map((heading) => <th key={heading} style={{ border: "1px solid #bbb", padding: 7 }}>{heading}</th>)}
          </tr>
        </thead>
        <tbody>
          {filteredRecords.length === 0 ? (
            <tr><td colSpan={12} style={{ padding: 25, textAlign: "center", border: "1px solid #ccc" }}>No transactions match the selected filters.</td></tr>
          ) : filteredRecords.map((record) => (
            <tr key={record.id} onClick={() => navigate(`/transactions/${record.id}`)} style={{ cursor: "pointer", background: selectedIds.has(record.id) ? "#eef4ff" : "white" }}>
              <td style={{ border: "1px solid #ccc", padding: 6, textAlign: "center" }} onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selectedIds.has(record.id)} onChange={() => toggleRow(record.id)} aria-label={`Select transaction ${record.id}`} /></td>
              <td style={{ border: "1px solid #ccc", padding: 6 }}>{record.date || "-"}</td>
              <td style={{ border: "1px solid #ccc", padding: 6 }}>{record.accountName || "-"}</td>
              <td style={{ border: "1px solid #ccc", padding: 6 }}>{record.expenseType || "-"}</td>
              <td style={{ border: "1px solid #ccc", padding: 6 }}>{record.description || "-"}</td>
              <td style={{ border: "1px solid #ccc", padding: 6, textAlign: "right" }}>{record.amountIn ? formatMoney(record.amountIn) : "-"}</td>
              <td style={{ border: "1px solid #ccc", padding: 6, textAlign: "right" }}>{record.amountOut ? formatMoney(record.amountOut) : "-"}</td>
              <td style={{ border: "1px solid #ccc", padding: 6, textAlign: "right" }}>{typeof record.balance === "number" ? formatMoney(record.balance) : "-"}</td>
              <td style={{ border: "1px solid #ccc", padding: 6 }}>{record.paidBy || "-"}</td>
              <td style={{ border: "1px solid #ccc", padding: 6 }}>{record.comment || "-"}</td>
              <td style={{ border: "1px solid #ccc", padding: 6 }}>{record.proofUrl ? <a href={record.proofUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>View</a> : "-"}</td>
              <td style={{ border: "1px solid #ccc", padding: 6 }}><button onClick={(e) => { e.stopPropagation(); handleDelete(record); }} style={{ ...buttonStyle, background: "#c62828", borderColor: "#c62828", padding: "5px 9px" }}>Delete</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
